'use client';

import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Stack, Typography } from '@mui/material';
import Switch from '@mui/material/Switch';
import { styled } from '@mui/material/styles';
import {
  AppPalettes,
  DiscCDPalettes,
  DiscCPalettes,
  DiscDIPalettes,
  DiscDPalettes,
  DiscIPalettes,
  DiscISPalettes,
  DiscSCPalettes,
  DiscSPalettes,
} from '@prismo-io/design-system';
import { ThemeProvider } from '@prismo-io/design-system/theme-provider';
import { AuraProfileCode, type AuraProfileCodeT } from '@prismo-io/schemas';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { type FC, useCallback, useMemo, useState } from 'react';
import { FocusElement } from '../focus-element';

type Props = {
  isAdapted?: boolean;
  userUrl: string;
  auraAdaptedId?: AuraProfileCodeT | null;
  auraAdaptedName?: string | null;
  auraNaturalId?: AuraProfileCodeT | null;
  auraNaturalName?: string | null;
};

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 60,
  height: 30,
  padding: 0,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': {
      width: 26,
    },
    '& .MuiSwitch-switchBase.Mui-checked': {
      transform: 'translateX(18px)',
    },
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    // color: 'primary.main',
    '&.Mui-checked': {
      transform: 'translateX(30px)',
      // color: 'primary.main',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.background.default,
      },
      '&.Mui-disabled': {
        opacity: 0.4,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 26,
    height: 26,
    borderRadius: 12,
    // color: 'red',
    color: theme.palette.primary.main,
    // backgroundColor: 'primary.main',
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 30 / 2,
    opacity: 1,
    backgroundColor: theme.palette.background.default,
    boxSizing: 'border-box',
  },
}));

export const AuraNaturalAdaptedSwitch: FC<Props> = (props: Props) => {
  const {
    userUrl,
    auraAdaptedId = AuraProfileCode.enum.PEACEMAKER,
    auraAdaptedName = 'Pacificateur',
    auraNaturalId = null,
    auraNaturalName = 'Conseiller',
  } = props;

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { i18n } = useLingui();

  const themeSwitch = useCallback((id: AuraProfileCodeT | null) => {
    switch (id) {
      case AuraProfileCode.Enum.ADVISOR:
        return DiscSPalettes.light;
      case AuraProfileCode.Enum.ANALYST:
        return DiscCPalettes.light;
      case AuraProfileCode.Enum.ANIMATOR:
        return DiscIPalettes.light;
      case AuraProfileCode.Enum.COACH:
        return DiscDIPalettes.light;
      case AuraProfileCode.Enum.PEACEMAKER:
        return DiscISPalettes.light;
      case AuraProfileCode.Enum.PILOT:
        return DiscDPalettes.light;
      case AuraProfileCode.Enum.PLANNER:
        return DiscCDPalettes.light;
      case AuraProfileCode.Enum.PROTECTOR:
        return DiscSCPalettes.light;
      default:
        return AppPalettes.light;
    }
  }, []);

  const naturalTheme = useMemo(() => {
    return themeSwitch(auraNaturalId);
  }, [auraNaturalId, themeSwitch, searchParams]);

  const adaptedTheme = useMemo(() => {
    return themeSwitch(auraAdaptedId);
  }, [auraAdaptedId, themeSwitch]);

  const isAdapted = useMemo(() => {
    return !searchParams.has('type') || searchParams.get('type') !== 'natural';
  }, [searchParams]);

  const [isAdaptedPosition, SetIsAdaptedPosition] = useState(isAdapted);

  const mainTheme = useMemo(() => {
    if (isAdapted) {
      return adaptedTheme;
    }

    return naturalTheme;
  }, [adaptedTheme, naturalTheme, isAdapted]);

  const onToggle = useCallback(() => {
    SetIsAdaptedPosition(!isAdapted);
    const params = new URLSearchParams(searchParams.toString());

    if (isAdapted) {
      params.set('type', 'natural');
    } else {
      params.delete('type');
    }

    return router.push(`${pathname}?${params.toString()}`, { scroll: false });
  }, [isAdapted, searchParams, pathname]);

  return (
    <FocusElement name="aura-adapted-natural" needFlex={false}>
      <ThemeProvider theme={mainTheme} options={{ key: 'aura-profile-switch' }}>
        <Box
          component="div"
          position="relative"
          zIndex={50}
          p={{ none: '4px 8px', md: 2 }}
          bgcolor="white"
          borderRadius={1_000_000}
          sx={{
            boxShadow: '2px 2px 7px 0px rgba(0,0,0,0.3)',
          }}
        >
          <Box
            component="div"
            position="absolute"
            top="0"
            bottom="0"
            right={isAdapted ? '0' : 'unset'}
            left={!isAdapted ? '0' : 'unset'}
            width={{ none: '7.5rem', md: '10.5rem' }}
            bgcolor="background.primary"
            borderRadius={1_000_000}
          />
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            position="relative"
          >
            <ThemeProvider
              theme={naturalTheme}
              options={{ key: 'aura-profile-switch-natural' }}
            >
              <Stack
                direction="column"
                alignItems="center"
                width={{ none: '4rem', md: '6.5rem' }}
              >
                <Typography
                  lineHeight={1.2}
                  sx={{ display: { none: 'none', md: 'inline-block' } }}
                >
                  <Trans>Profil naturel</Trans>
                </Typography>
                <Typography
                  lineHeight={1.2}
                  fontWeight="bolder"
                  color="primary.main"
                  sx={{ display: { none: 'inline-block', md: 'none' } }}
                >
                  <Trans>Naturel</Trans>
                </Typography>
                {auraNaturalId && (
                  <Typography
                    lineHeight={1.2}
                    fontWeight="bolder"
                    color="primary.main"
                    sx={{ display: { none: 'none', md: 'inline-block' } }}
                  >
                    {auraNaturalName}
                  </Typography>
                )}
                {!auraNaturalId && (
                  <Typography
                    lineHeight={1.2}
                    fontStyle="italic"
                    sx={{ opacity: 0.75 }}
                  >
                    <Trans>Non calculé</Trans>
                  </Typography>
                )}
              </Stack>
            </ThemeProvider>
            <AntSwitch
              checked={isAdaptedPosition}
              onClick={onToggle}
              disabled={!auraNaturalId}
              inputProps={{ 'aria-label': 'ant design' }}
            />
            <ThemeProvider
              theme={adaptedTheme}
              options={{ key: 'aura-profile-switch-adapted' }}
            >
              <Stack
                direction="column"
                alignItems="center"
                width={{ none: '4rem', md: '6.5rem' }}
              >
                <Typography
                  lineHeight={1.2}
                  sx={{ display: { none: 'none', md: 'inline-block' } }}
                >
                  <Trans>Profil adapté</Trans>
                </Typography>
                <Typography
                  lineHeight={1.2}
                  fontWeight="bolder"
                  color="primary.main"
                  sx={{ display: { none: 'inline-block', md: 'none' } }}
                >
                  <Trans>Adapté</Trans>
                </Typography>
                <Typography
                  lineHeight={1.2}
                  fontWeight="bolder"
                  color="primary.main"
                  sx={{ display: { none: 'none', md: 'inline-block' } }}
                >
                  {auraAdaptedName}
                </Typography>
              </Stack>
            </ThemeProvider>
          </Stack>
        </Box>
      </ThemeProvider>
    </FocusElement>
  );
};
